import type * as THREE from 'three'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'

/**
 * 校验结果结构，聚合错误与警告信息。
 */
export interface ValidationResult {
  /** 是否通过校验（errors 为空） */
  valid: boolean
  /** 致命错误信息列表 */
  errors: string[]
  /** 非致命警告信息列表 */
  warnings: string[]
}

/**
 * Splat 相关数据与运行时环境的校验工具集合。
 */
export class SplatValidator {
  /**
   * 验证 PLY Header 格式。
   *
   * @param header - PLY 文件头文本
   * @returns 含 errors/warnings 的校验结果
   */
  static validatePlyHeader(header: string): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // 检查PLY签名
    if (!header.startsWith('ply')) {
      errors.push('文件不是有效的PLY格式：缺少 "ply" 签名')
    }

    // 检查格式类型
    if (!header.includes('format binary_little_endian')) {
      if (header.includes('format ascii')) {
        errors.push('不支持ASCII格式PLY，请使用binary_little_endian格式')
      } else if (header.includes('format binary_big_endian')) {
        errors.push('不支持big_endian格式，请使用binary_little_endian格式')
      }
    }

    // 检查必需属性
    const requiredProps = [
      'x',
      'y',
      'z',
      'rot_0',
      'rot_1',
      'rot_2',
      'rot_3',
      'scale_0',
      'scale_1',
      'scale_2',
      'opacity',
    ]

    for (const prop of requiredProps) {
      if (
        !header.includes(`property float ${prop}`) &&
        !header.includes(`property double ${prop}`)
      ) {
        errors.push(`缺少必需属性: ${prop}`)
      }
    }

    // 检查splat数量
    const match = header.match(/element vertex (\d+)/)
    if (!match) {
      errors.push('无法解析splat数量')
    } else {
      const count = parseInt(match[1], 10)
      if (count > 10_000_000) {
        warnings.push(`splat数量过多(${count.toLocaleString()})，可能导致内存不足`)
      }
      if (count === 0) {
        errors.push('splat数量为0')
      }
    }

    // 检查颜色属性
    if (!header.includes('f_dc_0')) {
      warnings.push('缺少颜色属性f_dc_0，将使用默认颜色')
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * 验证 SplatData 数据完整性。
   *
   * @param data - 待校验的 SplatData
   * @returns 含 errors/warnings 的校验结果
   */
  static validateSplatData(data: SplatData): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // 检查基本数据
    if (data.numSplats === 0) {
      errors.push('SplatData为空')
      return { valid: false, errors, warnings }
    }

    // 检查位置数据
    const x = data.getProp('x')
    const y = data.getProp('y')
    const z = data.getProp('z')
    if (!x || !y || !z) {
      errors.push('缺少位置数据(x, y, z)')
    } else {
      // 检查是否有NaN或Infinity
      let invalidCount = 0
      for (let i = 0; i < data.numSplats; i++) {
        if (!Number.isFinite(x[i]) || !Number.isFinite(y[i]) || !Number.isFinite(z[i])) {
          invalidCount++
        }
      }
      if (invalidCount > 0) {
        warnings.push(`发现${invalidCount}个位置包含NaN或Infinity值`)
      }
    }

    // 检查四元数归一化
    const rot0 = data.getProp('rot_0')
    const rot1 = data.getProp('rot_1')
    const rot2 = data.getProp('rot_2')
    const rot3 = data.getProp('rot_3')

    if (rot0 && rot1 && rot2 && rot3) {
      let unnormalizedCount = 0
      for (let i = 0; i < data.numSplats; i++) {
        const len = Math.sqrt(rot0[i] ** 2 + rot1[i] ** 2 + rot2[i] ** 2 + rot3[i] ** 2)
        if (Math.abs(len - 1.0) > 0.01) {
          unnormalizedCount++
        }
      }
      if (unnormalizedCount > 0) {
        warnings.push(`${unnormalizedCount}个四元数未归一化，建议启用normalizeQuaternions选项`)
      }
    }

    // 检查透明度范围
    const opacity = data.getProp('opacity')
    if (opacity) {
      let lowAlphaCount = 0
      for (let i = 0; i < data.numSplats; i++) {
        // opacity是sigmoid前的值，sigmoid(x) < 0.01 对应 x < -4.6
        if (opacity[i] < -4.6) {
          lowAlphaCount++
        }
      }
      if (lowAlphaCount > data.numSplats * 0.1) {
        warnings.push(
          `${lowAlphaCount}个splat(${((lowAlphaCount / data.numSplats) * 100).toFixed(1)}%)的透明度很低，建议启用alphaRemovalThreshold过滤`
        )
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * 检测 WebGL 能力。
   *
   * @param renderer - 当前 WebGLRenderer
   * @returns 含 errors/warnings 的校验结果
   */
  static checkWebGLCapabilities(renderer: THREE.WebGLRenderer): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    const gl = renderer.getContext()

    // 检查WebGL版本
    const isWebGL2 = gl instanceof WebGL2RenderingContext
    if (!isWebGL2) {
      warnings.push('WebGL2不可用，部分功能可能受限')
    }

    // 检查浮点纹理支持
    if (!isWebGL2) {
      const floatTextureExt = gl.getExtension('OES_texture_float')
      if (!floatTextureExt) {
        errors.push('不支持浮点纹理，无法渲染3DGS')
      }
    }

    // 检查最大纹理尺寸
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE)
    if (maxTextureSize < 4096) {
      warnings.push(`最大纹理尺寸为${maxTextureSize}，可能限制splat数量`)
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * 估计内存需求。
   *
   * @param numSplats - splat 数量
   * @returns CPU/GPU 估算占用（MB）以及是否在推荐范围内
   */
  static estimateMemoryRequirements(numSplats: number): {
    cpuMemoryMB: number
    gpuMemoryMB: number
    recommended: boolean
  } {
    // CPU内存：每个splat约60字节（位置、旋转、缩放、颜色、SH）
    const cpuBytesPerSplat = 60
    const cpuMemoryMB = (numSplats * cpuBytesPerSplat) / (1024 * 1024)

    // GPU内存：数据纹理
    // transformA: 4 * 4 bytes = 16 bytes/splat
    // transformB: 4 * 4 * 2 = 32 bytes/splat (双高纹理)
    // colorTexture: 4 bytes/splat
    // orderTexture: 4 bytes/splat
    const gpuBytesPerSplat = 16 + 32 + 4 + 4
    const gpuMemoryMB = (numSplats * gpuBytesPerSplat) / (1024 * 1024)

    // 推荐：CPU < 500MB, GPU < 1GB
    const recommended = cpuMemoryMB < 500 && gpuMemoryMB < 1000

    return {
      cpuMemoryMB: Math.round(cpuMemoryMB * 10) / 10,
      gpuMemoryMB: Math.round(gpuMemoryMB * 10) / 10,
      recommended,
    }
  }
}
